/**
 * Google Drive Utilities for Frontend
 * Handles OAuth token acquisition, Google Picker, and file listing/validation
 */

// Types
export interface GDriveFile {
  id: string;
  name: string;
  mimeType: string;
  size: number;
}

export interface GDriveFolder {
  id: string;
  name: string;
}

export interface FileValidationResult {
  validFiles: GDriveFile[];
  invalidFiles: GDriveFile[];
  oversizedFiles: GDriveFile[];
}

// Declare Google types for TypeScript
declare global {
  interface Window {
    google: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: TokenResponse) => void;
          }) => TokenClient;
        };
      };
      picker: {
        PickerBuilder: new () => PickerBuilder;
        DocsView: new (viewId: string) => DocsView;
        ViewId: {
          DOCS: string;
          FOLDERS: string;
        };
        Feature: {
          MULTISELECT_DISABLED: string;
          MULTISELECT_ENABLED: string;
        };
        Action: {
          PICKED: string;
          CANCEL: string;
        };
        Response: {
          ACTION: string;
          DOCUMENTS: string;
        };
      };
    };
    gapi: {
      load: (api: string, callback: () => void) => void;
      client: {
        init: (config: { apiKey?: string; discoveryDocs?: string[] }) => Promise<void>;
        setToken: (token: { access_token: string }) => void;
        drive: {
          files: {
            list: (params: {
              q: string;
              fields: string;
              pageSize?: number;
            }) => Promise<{ result: { files: GDriveFileRaw[] } }>;
          };
        };
      };
    };
  }
}

interface TokenResponse {
  access_token: string;
  error?: string;
}

interface TokenClient {
  requestAccessToken: (options?: { prompt?: string }) => void;
}

interface PickerBuilder {
  addView: (view: DocsView) => PickerBuilder;
  enableFeature: (feature: string) => PickerBuilder;
  setOAuthToken: (token: string) => PickerBuilder;
  setDeveloperKey: (key: string) => PickerBuilder;
  setAppId: (appId: string) => PickerBuilder;
  setTitle: (title: string) => PickerBuilder;
  setCallback: (callback: (data: PickerCallbackData) => void) => PickerBuilder;
  build: () => { setVisible: (visible: boolean) => void };
}

interface DocsView {
  setIncludeFolders: (include: boolean) => DocsView;
  setSelectFolderEnabled: (enabled: boolean) => DocsView;
  setMimeTypes: (mimeTypes: string) => DocsView;
}

interface PickerCallbackData {
  [key: string]: string | PickerDocument[] | PickerDocumentWithSize[];
}

interface PickerDocument {
  id: string;
  name: string;
  mimeType: string;
}

interface PickerDocumentWithSize {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes?: number;
}

interface GDriveFileRaw {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
}

// All attachment allow-lists live in attachment-types.ts — the constants
// below re-export through that single source of truth. We add the Google
// Drive-specific MIME types (google-apps documents) on top of the shared
// allow-list so the picker can show native Drive docs alongside file uploads.
import {
  ALLOWED_ATTACHMENT_EXTENSIONS,
  ALLOWED_ATTACHMENT_MIME_TYPES,
  MAX_ATTACHMENT_SIZE_BYTES,
} from './attachment-types';

const ALLOWED_EXTENSIONS = ALLOWED_ATTACHMENT_EXTENSIONS;
const ALLOWED_MIME_TYPES = [
  ...ALLOWED_ATTACHMENT_MIME_TYPES,
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.spreadsheet',
  'application/vnd.google-apps.presentation',
];

const MAX_FILE_SIZE = MAX_ATTACHMENT_SIZE_BYTES;

// State to track if APIs are loaded
let gapiLoaded = false;
let gisLoaded = false;

/**
 * Load Google API scripts (gapi + Google Identity Services)
 */
export function loadGoogleApis(apiKey?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Load GAPI (Google API Client)
    const loadGapi = new Promise<void>((resolveGapi) => {
      if (gapiLoaded) {
        resolveGapi();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => {
        window.gapi.load('client:picker', async () => {
          await window.gapi.client.init({
            apiKey: apiKey,
            discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'],
          });
          gapiLoaded = true;
          resolveGapi();
        });
      };
      script.onerror = () => reject(new Error('Failed to load Google API'));
      document.body.appendChild(script);
    });

    // Load GIS (Google Identity Services)
    const loadGis = new Promise<void>((resolveGis) => {
      if (gisLoaded) {
        resolveGis();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = () => {
        gisLoaded = true;
        resolveGis();
      };
      script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
      document.body.appendChild(script);
    });

    Promise.all([loadGapi, loadGis])
      .then(() => resolve())
      .catch(reject);
  });
}

/**
 * Initialize token client for OAuth
 */
export function initTokenClient(
  clientId: string,
  scopes: string,
  callback: (response: TokenResponse) => void
): TokenClient {
  return window.google.accounts.oauth2.initTokenClient({
    client_id: clientId,
    scope: scopes,
    callback,
  });
}

/**
 * Request access token (opens Google sign-in popup)
 */
export function requestAccessToken(tokenClient: TokenClient): void {
  tokenClient.requestAccessToken({ prompt: 'consent' });
}

/**
 * Open Google Picker for folder selection
 */
export function openGooglePicker(
  accessToken: string,
  apiKey: string,
  appId: string,
  onFolderSelected: (folder: GDriveFolder) => void,
  onCancel: () => void
): void {
  const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
    .setIncludeFolders(true)
    .setSelectFolderEnabled(true)
    .setMimeTypes('application/vnd.google-apps.folder');

  const picker = new window.google.picker.PickerBuilder()
    .addView(view)
    .enableFeature(window.google.picker.Feature.MULTISELECT_DISABLED)
    .setOAuthToken(accessToken)
    .setDeveloperKey(apiKey)
    .setAppId(appId)
    .setCallback((data: PickerCallbackData) => {
      const action = data[window.google.picker.Response.ACTION] as string;
      
      if (action === window.google.picker.Action.PICKED) {
        const documents = data[window.google.picker.Response.DOCUMENTS] as PickerDocument[];
        if (documents && documents.length > 0) {
          const selectedFolder = documents[0];
          onFolderSelected({
            id: selectedFolder.id,
            name: selectedFolder.name,
          });
        }
      } else if (action === window.google.picker.Action.CANCEL) {
        onCancel();
      }
    })
    .build();

  picker.setVisible(true);
}

/**
 * Open Google Picker for multiple file selection.
 * With drive.file scope, selected files are granted access to the app.
 */
export function openGoogleFilePicker(
  accessToken: string,
  apiKey: string,
  appId: string,
  onFilesSelected: (files: GDriveFile[]) => void,
  onCancel: () => void
): void {
  // Create a view for all supported file types
  const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
    .setIncludeFolders(true) // Allow navigating into folders
    .setSelectFolderEnabled(false) // But don't allow selecting folders
    // .setMimeTypes(ALLOWED_MIME_TYPES.join(','));

  const picker = new window.google.picker.PickerBuilder()
    .addView(view)
    .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
    .setOAuthToken(accessToken)
    .setDeveloperKey(apiKey)
    .setAppId(appId)
    .setTitle('Select files to sync')
    .setCallback((data: PickerCallbackData) => {
      const action = data[window.google.picker.Response.ACTION] as string;
      
      if (action === window.google.picker.Action.PICKED) {
        const documents = data[window.google.picker.Response.DOCUMENTS] as PickerDocumentWithSize[];
        if (documents && documents.length > 0) {
          const files: GDriveFile[] = documents.map((doc) => ({
            id: doc.id,
            name: doc.name,
            mimeType: doc.mimeType,
            size: doc.sizeBytes || 0,
          }));
          onFilesSelected(files);
        }
      } else if (action === window.google.picker.Action.CANCEL) {
        onCancel();
      }
    })
    .build();

  picker.setVisible(true);
}

/**
 * List files in a Google Drive folder
 */
export async function listFolderFiles(
  accessToken: string,
  folderId: string
): Promise<GDriveFile[]> {
  // Set the access token for API calls
  window.gapi.client.setToken({ access_token: accessToken });

  const response = await window.gapi.client.drive.files.list({
    q: `'${folderId}' in parents and trashed = false and mimeType != 'application/vnd.google-apps.folder'`,
    fields: 'files(id, name, mimeType, size)',
    pageSize: 1000,
  });

  return (response.result.files || []).map((file: GDriveFileRaw) => ({
    id: file.id,
    name: file.name,
    mimeType: file.mimeType,
    size: parseInt(file.size || '0', 10),
  }));
}

/**
 * Validate Google Drive files against allowed types and size limits
 */
export function validateGDriveFiles(files: GDriveFile[]): FileValidationResult {
  const validFiles: GDriveFile[] = [];
  const invalidFiles: GDriveFile[] = [];
  const oversizedFiles: GDriveFile[] = [];

  files.forEach((file) => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      oversizedFiles.push(file);
      return;
    }

    // Get file extension
    const ext = file.name.toLowerCase().split('.').pop() || '';
    console.log(`Validating file: ${file.name}, extension: ${ext}, mimeType: ${file.mimeType}, size: ${file.size} bytes`);
    // Check by extension or MIME type
    if (
      (ALLOWED_EXTENSIONS as readonly string[]).includes(ext) ||
      (ALLOWED_MIME_TYPES as readonly string[]).includes(file.mimeType)
    ) {
      validFiles.push(file);
    } else {
      invalidFiles.push(file);
    }
  });

  return { validFiles, invalidFiles, oversizedFiles };
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Get supported file types string for display
 */
export { getSupportedAttachmentTypesString as getSupportedFileTypesString } from './attachment-types';

/**
 * Download a file from Google Drive and return as Blob
 * This uses the browser's access token which has direct user authorization
 */
export async function downloadGDriveFile(
  accessToken: string,
  fileId: string
): Promise<Blob> {
  const response = await fetch(
    `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
  }

  return response.blob();
}
